import { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
  useTheme,
  Badge,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import MenuIcon from '@mui/icons-material/Menu';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { UserMenuItems } from '../Profile/UserMenuItems';
import logout from '../Authorization/Logout';
import CustomAvatar from '../Profile/CustomAvatar';
import SiteLogo from '../../shared/images/ColoredBackGroundLogo.svg';
import { useSelector, useDispatch } from 'react-redux';
import * as UserActions from '../Authorization/userSlice';
import getFirstPartOfPath from '../../utils/getFirstPartOfPath';
import { RouteInformation } from '../../shared/constants/RouteInformation';
import { getNotificationsForCurrentUser } from '../../shared/requests/notifications';
import { useQuery } from '@tanstack/react-query';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import { neutralPalette } from '../../theme/palette';
import { trackerFilterTypographySx, trackerPopupPaperSx } from '../TrackerFilter/trackerFilterToolbarStyles';

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  header: {
    height: '60px',
    backgroundColor: neutralPalette.white,
    color: neutralPalette.textPrimary,
    borderBottom: `1px solid ${neutralPalette.borderSubtle}`,
    boxShadow: neutralPalette.shadowSoft,
  },
  toolbar: {
    minHeight: '60px',
    color: neutralPalette.textPrimary,
    fontFamily: "'Open Sans', sans-serif",
    paddingLeft: 'clamp(16px, 2.6vw, 34px)',
    paddingRight: '18px',
    [breakpoints.down('sm')]: {
      paddingLeft: '16px',
      paddingRight: '8px',
    },
  },
  logoBox: {
    position: 'absolute',
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'center',
    zIndex: 0,
  },
  logoImg: {
    maxWidth: '124px',
    maxHeight: '37.98px',
  },
  sidebarBtn: {
    padding: '8px',
    '&:focus': {
      backgroundColor: 'inherit',
    },
    zIndex: 2,
  },
  leftItems: {
    display: 'flex',
    alignItems: 'center',
  },
  rightItems: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginRight: '32px',
    [breakpoints.down('md')]: {
      marginRight: '20px',
      gap: '10px',
    },
    [breakpoints.down('sm')]: {
      marginRight: '8px',
      gap: '8px',
    },
  },
  notificationBtn: {
    width: '32px',
    height: '32px',
    padding: '2px',
  },
  notificationBtnIcon: {
    '&:hover': { color: neutralPalette.textPrimary },
    color: neutralPalette.textSecondary,
    fontSize: '28px',
  },
  userProfileWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  pageTitle: {
    fontFamily: '"Open Sans", Arial, sans-serif',
    color: neutralPalette.textPrimary,
    marginLeft: '14px',
    fontSize: '18px',
    fontWeight: 400,
    lineHeight: 1.2,
    letterSpacing: 'normal',
    display: 'inline-flex',
    alignItems: 'center',
    [breakpoints.down('md')]: {
      marginLeft: '12px',
    },
  },
}));

function Header(props) {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { sidebarOpen, onSetSidebarOpen } = props;
  const [anchorElUser, setAnchorElUser] = useState(null);
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const classes = useStyles();

  const [notificationsCount, setNotificationsCount] = useState(0);
  const { data: notifications } = useQuery({
    queryKey: ['notifications', 'header'],
    queryFn: () => getNotificationsForCurrentUser(),
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (notifications) {
      setNotificationsCount(notifications.filter((notification) => notification.isRead === false).length ?? 0);
    }
  }, [notifications]);

  const handleOpenUserMenu = (e) => {
    setAnchorElUser(e.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const getTab = () => {
    switch (getFirstPartOfPath(pathname)) {
      case RouteInformation.mediaInteraction.path:
        return 'Media Interactions';
      case RouteInformation.teamAnalytics.path:
        return 'Team Analytics';
      case RouteInformation.customAnalytics.path:
        return 'Custom Analytics';
      case RouteInformation.serviceLog.path:
        return 'Service Log';
      case RouteInformation.engagementLog.path:
        return 'Engagement Log';
      case RouteInformation.contentLog.path:
        return 'Content Log';
      case RouteInformation.admin.path:
        return 'Admin';
      case RouteInformation.profile.path:
        return 'User Profile';
      case RouteInformation.tenants.path:
        return 'Broadsight Admin';
      case RouteInformation.contacts.path:
        return 'Contacts';
      case RouteInformation.calendar.path:
        return 'Calendar';
      case RouteInformation.report.path:
        return 'Report Generation';
      case RouteInformation.notifications.path:
        return 'Notifications';
      case RouteInformation.collaboration.path:
        return 'Collaborate';
      case RouteInformation.broadcastPopup.path:
        return 'Announcement';
      default:
        return 'Page Name Placeholder';
    }
  };

  const clearUserInfo = () => {
    dispatch(UserActions.userLogout());
  };
  const currentTab = getTab();

  return (
    <AppBar color="default" position="fixed" elevation={0} className={classes.header}>
      <Toolbar className={classes.toolbar}>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={() => {
            onSetSidebarOpen(!sidebarOpen);
          }}
          className={classes.sidebarBtn}
        >
          <MenuIcon />
        </IconButton>
        {!isMobile && (
          <Typography variant="h6" noWrap className={classes.pageTitle}>
            {currentTab}
          </Typography>
        )}

        <div className={classes.logoBox}>
          <img src={SiteLogo} alt="logo" className={classes.logoImg} />
        </div>

        <div className={classes.rightItems}>
          <IconButton
            onClick={() => {
              if (pathname === RouteInformation.notifications.path) {
                navigate(0); // refresh page
              } else {
                navigate(RouteInformation.notifications.path, { replace: true });
              }
            }}
            className={classes.notificationBtn}
          >
            <Tooltip title="Notifications">
              <Badge badgeContent={notificationsCount} color="error">
                <NotificationsOutlinedIcon fontSize="inherit" className={classes.notificationBtnIcon} />
              </Badge>
            </Tooltip>
          </IconButton>

          <div className={classes.userProfileWrapper}>
            <Tooltip title="Open settings">
              <IconButton
                onClick={handleOpenUserMenu}
                sx={{
                  p: 0,
                  '& .MuiAvatar-root': {
                    width: 28,
                    height: 28,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                  },
                }}
                data-cy="user-menu-button"
              >
                <CustomAvatar user={user.userInfo} />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        <Menu
          id="menu-appbar"
          anchorEl={anchorElUser}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorElUser)}
          onClose={handleCloseUserMenu}
          slotProps={{
            paper: {
              sx: {
                ...trackerPopupPaperSx,
                minWidth: 190,
                mt: 0.25,
                borderRadius: '12px',
                overflow: 'hidden',
              },
            },
          }}
        >
          {UserMenuItems.map((item) => (
            <MenuItem
              key={item.title}
              component={Link}
              to={item.link}
              onClick={handleCloseUserMenu}
              sx={{
                ...trackerFilterTypographySx,
                minHeight: 40,
                px: 1.5,
                color: neutralPalette.textPrimary,
                '&:hover': {
                  backgroundColor: neutralPalette.surfaceMuted,
                },
              }}
            >
              <Typography sx={trackerFilterTypographySx}>{item.title}</Typography>
            </MenuItem>
          ))}
          <MenuItem
            key="logoutItem"
            onClick={() => {
              handleCloseUserMenu();
              logout(user.userInfo.username, clearUserInfo);
            }}
            data-cy="logout-item"
            sx={{
              ...trackerFilterTypographySx,
              minHeight: 40,
              px: 1.5,
              color: neutralPalette.textPrimary,
              borderTop: `1px solid ${neutralPalette.borderSubtle}`,
              '&:hover': {
                backgroundColor: neutralPalette.surfaceMuted,
              },
            }}
          >
            <Typography sx={trackerFilterTypographySx}>Log Out</Typography>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}

export default Header;
